import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authApi } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import { AppBrandMark } from '../components/common/AppBrandMark';
import { useAppLogo } from '../hooks/useAppLogo';

const mapBackendErrorToTranslation = (msg: string, t: (k: string) => string) => {
  if (msg.includes('Password must be at least 6 characters')) return t('passwordComplexity');
  if (msg.includes('password must be longer')) return t('passwordComplexity');
  if (msg.includes('Email already exists')) return t('emailExists');
  if (msg.includes('Unauthorized')) return t('unauthorized');
  return msg;
};

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const { t, lang, setLang } = useLang();
  const logo = useAppLogo(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false,
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      navigate('/dashboard');
      return;
    }

    // Clear any previous error state
    setError('');
    
    // Persist language selection
    const storedLang = localStorage.getItem('lang');
    if (storedLang && storedLang !== lang) {
      setLang(storedLang as 'vi' | 'en');
    }

    // Load saved credentials if remember me was checked
    const savedUsername = localStorage.getItem('rememberedUsername');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    if (savedUsername && savedRememberMe) {
      setFormData(prev => ({
        ...prev,
        username: savedUsername,
        rememberMe: savedRememberMe
      }));
    }

    // Check if this is the first login attempt
    const checkFirstTime = async () => {
      try {
        const res = await authApi.firstTime();
        setIsFirstTime(res.firstTime);
      } catch (error) {
        setIsFirstTime(false);
      }
    };
    checkFirstTime();
    // eslint-disable-next-line
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if ((location.state as any)?.resetSuccess) {
      setResetSuccess(true);
      navigate('/login', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    localStorage.setItem('lang', lang);
  }, [lang]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (isFirstTime) {
        const response = await authApi.register(formData.username, formData.password, t('superadminName'), formData.username, 'SUPERADMIN');
        localStorage.setItem('token', response.access_token);
        await login(formData.username, formData.password);
        navigate('/dashboard');
      } else {
        await login(formData.username, formData.password);
        
        // Handle remember me functionality
        if (formData.rememberMe) {
          localStorage.setItem('rememberedUsername', formData.username);
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberedUsername');
          localStorage.removeItem('rememberMe');
        }
        
        navigate('/dashboard');
      }
    } catch (err: any) {
      let backendMsg = '';
      if (err?.response?.data?.error?.message) {
        if (Array.isArray(err.response.data.error.message)) {
          backendMsg = err.response.data.error.message.map((m: string) => mapBackendErrorToTranslation(m, t)).join(' ');
        } else {
          backendMsg = mapBackendErrorToTranslation(err.response.data.error.message, t);
        }
      }
      setError(
        backendMsg || (isFirstTime ? t('firstTimeError') : t('loginError'))
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          {/* Language Toggle */}
          <div className="flex justify-end mb-8">
            <div className="flex space-x-2">
              <button
                onClick={() => setLang('vi')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  lang === 'vi'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                VI
              </button>
              <button
                onClick={() => setLang('en')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  lang === 'en'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                EN
              </button>
            </div>
          </div>

          {/* Header */}
          <div>
            <AppBrandMark logo={logo} size="md" />
            
            <h2 className="mt-8 text-3xl font-bold tracking-tight text-gray-900">
              {isFirstTime ? t('createSuperadminTitle') : t('loginTitle')}
            </h2>
            
            {isFirstTime && (
              <p className="mt-2 text-sm text-gray-600">
                {t('firstTimePrompt')}
              </p>
            )}
          </div>

          {/* Login Form */}
          <div className="mt-8">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {resetSuccess && (
                <div className="rounded-xl bg-green-50 border border-green-200 p-4">
                  <div className="text-sm text-green-700">{t('resetPasswordSuccess')}</div>
                </div>
              )}
              {error && (
                <div className="rounded-xl bg-red-50 border border-red-200 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm text-red-700">{error}</div>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="username" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('usernameOrEmail')}
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="block w-full px-4 py-3 rounded-xl border border-gray-200 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 placeholder-gray-400"
                  placeholder={t('enterUsernameOrEmail')}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('password')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete={isFirstTime ? 'new-password' : 'current-password'}
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full px-4 py-3 rounded-xl border border-gray-200 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200"
                  placeholder="••••••••"
                />
              </div>

              {!isFirstTime && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="rememberMe"
                      name="rememberMe"
                      type="checkbox"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700 cursor-pointer">
                      {t('rememberMe')}
                    </label>
                  </div>

                  <div className="text-sm">
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
                    >
                      {t('forgotPassword')}
                    </button>
                  </div>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02]"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {isFirstTime ? t('creatingAccount') : t('signingIn')}
                    </div>
                  ) : (
                    isFirstTime ? t('createAccount') : t('signIn')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Right side - Background */}
      <div className="hidden lg:block relative w-0 flex-1">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="absolute inset-0 flex items-center justify-center p-12">
            <div className="text-center text-white max-w-lg">
              <AppBrandMark logo={logo} size="lg" variant="light" layout="stacked" />
              <div className="text-sm opacity-75 space-y-2">
                <p>✓ {t('loginFeatureSmartApartments')}</p>
                <p>✓ {t('loginFeatureModernPayments')}</p>
                <p>✓ {t('loginFeatureAccurateReports')}</p>
              </div>
            </div>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute top-20 left-20 w-32 h-32 bg-white bg-opacity-10 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 right-20 w-48 h-48 bg-white bg-opacity-5 rounded-full blur-2xl"></div>
          <div className="absolute top-1/2 left-1/4 w-20 h-20 bg-white bg-opacity-10 rounded-full blur-lg"></div>
        </div>
      </div>
    </div>
  );
}; 